import React from 'react';
import { Box, Typography, Container, alpha, keyframes, Breadcrumbs, Link } from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const meshMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const floatNode = keyframes`
  0%, 100% { transform: translateY(0) translateX(0); }
  25% { transform: translateY(-20px) translateX(10px); }
  75% { transform: translateY(10px) translateX(-15px); }
`;

const PageHeader = ({ title, subtitle }) => {
    const { lang, t } = useLanguage();
    const location = useLocation();
    const pathnames = location.pathname.split('/').filter((x) => x);

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: { xs: '350px', md: '500px' },
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                background: '#041e42',
                // Advanced Mesh Gradient for "15y Exp" look
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(45deg, 
                        ${alpha('#1D2558', 0.8)} 0%, 
                        ${alpha('#041e42', 0.9)} 40%, 
                        ${alpha('#DD8748', 0.1)} 70%, 
                        ${alpha('#1D2558', 0.8)} 100%)`,
                    backgroundSize: '400% 400%',
                    animation: `${meshMove} 15s ease infinite`,
                    zIndex: 0
                }
            }}
        >
            {/* Architectural Nodes & Lines (Tech Identity) */}
            <Box sx={{ position: 'absolute', inset: 0, opacity: 0.15, zIndex: 1, pointerEvents: 'none' }}>
                {[...Array(6)].map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            width: 2,
                            height: '100px',
                            background: `linear-gradient(to bottom, transparent, ${alpha('#DD8748', 0.5)}, transparent)`,
                            left: `${15 + i * 15}%`,
                            top: `${10 + i * 5}%`,
                            transform: `rotate(${30 + i * 20}deg)`,
                            animation: `${floatNode} ${8 + i}s ease-in-out infinite`,
                        }}
                    />
                ))}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '150%',
                        height: '150%',
                        transform: 'translate(-50%, -50%)',
                        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)',
                        backgroundSize: '60px 60px',
                    }}
                />
            </Box>

            {/* Content with Depth */}
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                <Box
                    sx={{
                        maxWidth: '800px',
                        mx: lang === 'ar' ? 'auto' : '0',
                        textAlign: lang === 'ar' ? 'center' : 'left'
                    }}
                >
                    {/* Professional Breadcrumb */}
                    <Box sx={{ mb: 4 }} data-aos="fade-down">
                        <Breadcrumbs
                            separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.3)' }} />}
                            sx={{ color: '#fff' }}
                        >
                            <Link
                                component={RouterLink}
                                to="/"
                                sx={{
                                    color: 'rgba(255,255,255,0.5)',
                                    textDecoration: 'none',
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    '&:hover': { color: '#DD8748' }
                                }}
                            >
                                {t('common.home').toUpperCase()}
                            </Link>
                            <Typography
                                sx={{
                                    color: '#DD8748',
                                    fontSize: '0.85rem',
                                    fontWeight: 800,
                                    letterSpacing: 1
                                }}
                            >
                                {title.toUpperCase()}
                            </Typography>
                        </Breadcrumbs>
                    </Box>

                    <Box sx={{ position: 'relative' }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 950,
                                color: '#fff',
                                fontSize: { xs: '3.5rem', md: '5.5rem' },
                                letterSpacing: -3,
                                lineHeight: 0.9,
                                mb: 3,
                                textShadow: '0 20px 40px rgba(0,0,0,0.3)',
                                position: 'relative',
                                display: 'inline-block'
                            }}
                            data-aos="fade-right"
                        >
                            {title}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -20,
                                    left: 0,
                                    width: '100px',
                                    height: '8px',
                                    bgcolor: '#DD8748',
                                    borderRadius: 4,
                                    display: { xs: 'none', md: 'block' }
                                }}
                            />
                        </Typography>

                        <Typography
                            variant="h5"
                            sx={{
                                color: alpha('#fff', 0.8),
                                fontWeight: 500,
                                maxWidth: '600px',
                                mt: 4,
                                lineHeight: 1.6,
                                fontStyle: 'italic',
                                borderLeft: `4px solid ${alpha('#DD8748', 0.5)}`,
                                pl: 3
                            }}
                            data-aos="fade-up"
                            data-aos-delay="200"
                        >
                            {subtitle}
                        </Typography>
                    </Box>
                </Box>
            </Container>

            {/* Decorative Overlay for contrast */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '30%',
                    background: 'linear-gradient(to top, #fff 0%, transparent 100%)',
                    zIndex: 3
                }}
            />
        </Box>
    );
};

export default PageHeader;
